/**
 * iOS 音声セッション keep-alive。
 *
 * iOS Safari は「走り続ける AudioContext（destination 接続）＝音声セッションが生きている」
 * 間だけ、<video> の音あり再生 (unmute) を許可する。
 * 当アプリはマイクをターンごとに開閉するため、応答クリップ到着時にはセッションが切れて
 * unmute が拒否され、再生が止まる（blob クリップが currentTime=0 で play/pause ループ）。
 *
 * そこで、最初のユーザー操作（マイクタップ等）で無音の AudioContext を1つ生成し、
 * 面接中ずっと走らせ続けることで音声セッションを維持する。
 * 参照アーキテクチャ (zenryokukikai/clone-package) がマイク常駐で達成しているのと同じ効果を、
 * 録音パイプラインに手を入れずに得るための最小実装。
 */

type AudioContextCtor = typeof AudioContext;

let keepAliveCtx: AudioContext | null = null;
let keepAliveSource: AudioBufferSourceNode | null = null;

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  return window.AudioContext || (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext || null;
}

/**
 * 音声セッションの維持を開始する。必ずユーザー操作（クリック/タップ）のハンドラ内から
 * 同期的に呼び出すこと（iOS は AudioContext の起動にユーザー操作を要求するため）。
 * 既に起動済みなら resume のみ行う。
 */
export function startAudioSessionKeepAlive(): void {
  const Ctor = resolveAudioContextCtor();
  if (!Ctor) return;

  try {
    if (!keepAliveCtx) {
      keepAliveCtx = new Ctor();
      // 無音バッファをループ再生して destination に出力し続ける。
      const buffer = keepAliveCtx.createBuffer(1, 1, keepAliveCtx.sampleRate);
      const source = keepAliveCtx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;
      const gain = keepAliveCtx.createGain();
      gain.gain.value = 0; // 完全無音（セッション維持のみが目的）
      source.connect(gain).connect(keepAliveCtx.destination);
      source.start(0);
      keepAliveSource = source;
    }

    if (keepAliveCtx.state === "suspended") {
      void keepAliveCtx.resume();
    }
  } catch {
    // keep-alive の失敗は致命ではない（再生は試みられる）。
  }
}

/**
 * keep-alive 用の AudioContext を返す（ユーザー操作で生成・resume 済み）。
 * 応答音声を Web Audio で audible に再生するための出力先として使う。
 */
export function getKeepAliveAudioContext(): AudioContext | null {
  return keepAliveCtx;
}

/** タブが前面復帰したときなどに suspended なら resume する。 */
export function resumeAudioSessionKeepAlive(): void {
  if (keepAliveCtx && keepAliveCtx.state === "suspended") {
    try {
      void keepAliveCtx.resume();
    } catch {
      // ignore
    }
  }
}

/** 面接終了・アンマウント時に破棄する。 */
export function stopAudioSessionKeepAlive(): void {
  try {
    keepAliveSource?.stop();
  } catch {
    // ignore
  }
  keepAliveSource = null;

  if (keepAliveCtx) {
    try {
      void keepAliveCtx.close();
    } catch {
      // ignore
    }
    keepAliveCtx = null;
  }
}
